import React from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import './News.css';
import useNews from '../hooks/useNews';
import { excerptText } from '../lib/newsText';

// Community news is authored in Markdown (edited from the admin News manager)
// and rendered here — shared verbatim by the home News section and the /news
// page so an article looks identical wherever it appears.

// Format the stored 'YYYY-MM-DD' date as e.g. "July 20, 2026". Parsed as a
// local calendar date (not `new Date('YYYY-MM-DD')`, which is UTC and can shift
// a day in western time zones).
function formatDate(value) {
  if (!value) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  const d = m
    ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

// Restrict Markdown to a safe inline/block subset (react-markdown blocks raw
// HTML by default). Headings are downshifted so an article's own heading never
// competes with the card's title (an <h3>); links open in a new tab; images are
// limited to http(s). Mirrors the sanitizing approach used for event
// descriptions in Sections.js.
const MD_ALLOWED = [
  'p', 'a', 'em', 'strong', 'ul', 'ol', 'li', 'br', 'code', 'pre', 'blockquote',
  'img', 'hr', 'del', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
];
const HEADING_TAG = { h1: 'h4', h2: 'h4', h3: 'h5', h4: 'h6', h5: 'h6', h6: 'h6' };
const makeHeading = (mdTag) => {
  const Tag = HEADING_TAG[mdTag];
  return function Heading({ node: _node, ...props }) {
    return <Tag className={`news-h news-${mdTag}`} {...props} />;
  };
};
const MD_COMPONENTS = {
  a({ node: _node, href, ...props }) {
    const external = /^https?:\/\//i.test(href || '');
    const tabProps = external ? { target: '_blank', rel: 'noopener noreferrer' } : {};
    // eslint-disable-next-line jsx-a11y/anchor-has-content
    return <a href={href} {...tabProps} {...props} />;
  },
  img({ node: _node, src, alt, ...props }) {
    // Only render http(s) images (blocks data:/javascript: and other schemes).
    if (!/^https?:\/\//i.test(src || '')) return null;
    return <img src={src} alt={alt || ''} loading="lazy" className="news-img" {...props} />;
  },
  h1: makeHeading('h1'),
  h2: makeHeading('h2'),
  h3: makeHeading('h3'),
  h4: makeHeading('h4'),
  h5: makeHeading('h5'),
  h6: makeHeading('h6'),
};

// A single news article. Shared by the home section, the /news page, and the
// admin preview. With `shortened`, it renders a plain-text teaser (used by the
// home preview); otherwise it renders the full Markdown body.
export const NewsArticle = ({ article, shortened = false }) => (
  <article className="glass card news-article">
    <header className="news-article-head">
      {article.date && (
        <time className="news-date" dateTime={article.date}>
          {formatDate(article.date)}
        </time>
      )}
      <h3 className="news-title">{article.title}</h3>
    </header>
    {shortened
      ? article.content && <p className="news-excerpt">{excerptText(article.content)}</p>
      : article.content && (
          <div className="news-body">
            <ReactMarkdown
              remarkPlugins={[remarkGfm, remarkBreaks]}
              allowedElements={MD_ALLOWED}
              unwrapDisallowed
              components={MD_COMPONENTS}
            >
              {article.content}
            </ReactMarkdown>
          </div>
        )}
  </article>
);

// Status notes + the stacked list of articles. `articles` are the ones to show
// (the caller slices/pages); `status` drives the notes. With `shortened`, each
// article renders as a teaser (home preview). `emptyNote` overrides the "no
// news" copy — the /news page uses it to say results are filtered out. Shared
// by the home News section and the /news page.
export const NewsList = ({ articles, status, shortened = false, emptyNote }) => (
  <>
    {status === 'loading' && <p className="news-note">Loading news…</p>}
    {status === 'signedOut' && (
      <p className="news-note">
        Community news is for members. <Link to="/login">Sign in or join</Link> to read the latest.
      </p>
    )}
    {status === 'error' && (
      <p className="news-note">News is unavailable right now — please check back soon.</p>
    )}
    {status === 'ready' && articles.length === 0 && (
      <p className="news-note">{emptyNote || 'No news yet — check back soon!'}</p>
    )}

    {articles.length > 0 && (
      <div className="news-list">
        {articles.map((a) => (
          <NewsArticle key={a.id} article={a} shortened={shortened} />
        ))}
      </div>
    )}
  </>
);

// Date-range filter for the news feed. Two date inputs plus quick presets.
// Lives in the shared News module so any news view can reuse it; the /news page
// is the current consumer. `from`/`to` are 'YYYY-MM-DD' (empty = unbounded).
export const NewsFilters = ({ from, to, presetDays, onFrom, onTo, onPreset, count }) => (
  <div className="news-filters">
    <div className="news-filter-presets" role="group" aria-label="Quick date ranges">
      {NEWS_PRESETS.map((p) => (
        <button
          key={p.label}
          type="button"
          className={`news-filter-chip${presetDays === p.days ? ' is-active' : ''}`}
          aria-pressed={presetDays === p.days}
          onClick={() => onPreset(p.days)}
        >
          {p.label}
        </button>
      ))}
    </div>
    <div className="news-filter-dates">
      <label className="news-filter-field">
        <span>From</span>
        <input type="date" value={from} max={to || undefined} onChange={(e) => onFrom(e.target.value)} />
      </label>
      <label className="news-filter-field">
        <span>To</span>
        <input type="date" value={to} min={from || undefined} onChange={(e) => onTo(e.target.value)} />
      </label>
    </div>
    {typeof count === 'number' && (
      <p className="news-filter-count">
        {count} {count === 1 ? 'article' : 'articles'}
      </p>
    )}
  </div>
);

// Quick-range presets for NewsFilters. `days: null` means "all time".
export const NEWS_PRESETS = [
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
  { label: 'Last year', days: 365 },
  { label: 'All time', days: null },
];

// How many articles the home page previews (shortened) before "View all news".
const HOME_NEWS = 2;

// Home-page News section: a shortened preview of the two latest articles with a
// button through to the full /news page.
export const News = () => {
  const { articles, status } = useNews({ max: HOME_NEWS });
  return (
    <section id="news" className="section">
      <div className="section-head">
        <h2>Community News</h2>
        <p className="section-lead">Updates and stories from the Dutchess County Bhabani community.</p>
      </div>

      <NewsList articles={articles.slice(0, HOME_NEWS)} status={status} shortened />

      {status === 'ready' && articles.length > 0 && (
        <div className="news-cta">
          <Link className="btn-primary" to="/news">View all news</Link>
        </div>
      )}
    </section>
  );
};

export default News;
